/** @jsx jsx */
import { Button } from 'components/buttons/Button'
import Form from 'components/global/Form'
import { Line } from 'components/Line'
import { EventFeatureImage } from 'components/media/EventFeatureImage'
import { EventMultiImage } from 'components/media/EventMultiImage'
import { MuxVideo } from 'components/media/MuxVideo'
import { Accordion } from 'components/modules/Accordion'
import { PageLayout } from 'components/PageLayout'
import { DefaultBlocks } from 'components/sanity/DefaultBlocks'
import { useIsSlug } from 'hooks/useIsSlug'
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import { jsx, Text, Box, Flex, Grid } from 'theme-ui'
import {
  eventQuery,
  eventSlugsQuery,
  winterfeastEventSlugsQuery,
} from '../../lib/sanity/queries/event'
import { usePreviewSubscription } from '../../lib/sanity/sanity'
import { sanityClient, getClient } from '../../lib/sanity/sanity.server'
import { useGlobalStaticData } from 'hooks/useGlobalStaticData'
import Link from 'next/link'
import { ExclusiveCross } from 'components/global/icons/ExclusiveCross'
import { CityOfHobartLogo } from 'components/global/svgs/CityOfHobartLogo'
import { useBreakpointIndex } from '@theme-ui/match-media'
import { WorldCross } from 'components/global/icons/WorldCross'
import { WinterFeast } from 'components/modules/WinterFeast'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(utc)
dayjs.extend(timezone)

export default function Event({ data = {}, preview }) {
  const router = useRouter()
  const slug = data?.event?.slug
  const isCorrectSlug = useIsSlug(slug?.current)

  const {
    data: { event },
  } = usePreviewSubscription(eventQuery, {
    params: { slug },
    initialData: data,
    enabled: preview && slug,
  })

  const {
    global: { siteSettings },
  } = useGlobalStaticData()

  const {
    tnewUrl,
    ticketsOnSale,
    ticketsOnSaleDate,
    ticketsOnSaleDateCopy,
    ticketsOffSaleFormTitle,
    ticketsOffSaleCopy,
    ticketsOffSaleRegistrationCopy,
    waitlistForm,
    resellForm,
    ballotForm,
    ballotOffSaleNote,
    subscribeForm,
  } = siteSettings

  //const ticketsOnSale = true

  if (router.isFallback && !slug) {
    return <ErrorPage statusCode={404} />
  }

  const today = dayjs()
  const upcoming =  event?.eventDates?.filter(event => dayjs(event.eventStart).isAfter(today)).length > 0

  const anySoldOut = event?.eventDates
    ? event.eventDates.filter(event => event.soldOut).length > 0
    : false
  const soldOut = event?.eventDates
    ? event.eventDates.filter(event => event.soldOut).length ==
      event.eventDates.length
    : false
  const soldOutText = event?.soldOutButtonText || 'Sold Out'
  const buyTicketsText = event?.ticketLinkButtonText || 'Buy Tickets'

  let buyTicketsLink = null

  if (event && !soldOut && event.tnewProductionSeason && event.eventDates) {
    const availablePerfs = event.eventDates.filter(event => !event.soldOut)

    buyTicketsLink = tnewUrl
    if (availablePerfs.length === 1) {
      buyTicketsLink +=
        '/' +
        event.tnewProductionSeason +
        '/' +
        availablePerfs[0].tnewPerformanceNumber
    } else {
      buyTicketsLink += '/overview/' + event.tnewProductionSeason
    }
  }

  const noWaitlist = event?.noWaitlist
  const noResell = event?.noResell

  const showImage =
    ((!event.eventVideo || !event.mobileEventVideo) && event.eventImage) ||
    event.disableVideo === true

  // Create metafields object to pass to PageLayout component
  // If there is no metatitle use event title
  const metaFields = {
    ...data?.event?.metaFields,
    metaTitle: data?.event?.metaFields?.metaTitle
      ? data.event.metaFields.metaTitle
      : data?.event?.title
      ? data.event.title
      : '',
  }

  const overrides = event.overrides === null ? {} : event.overrides
  const hasOverrideAccessiblity = overrides?.overrideAccessibility
  const hasOverrideAccessiblityCopy = !!overrides.overrideAccessibilityCopy

  let hasAccessibility = false
  event?.eventLocations?.forEach(({ accessibilityCopy }) => {
    if (accessibilityCopy) {
      hasAccessibility = true
    }
  })

  const bp = useBreakpointIndex({ defaultIndex: 2 })

  return (
    <PageLayout
      preview={preview}
      isCorrectSlug={isCorrectSlug}
      metaFields={metaFields}
      eventId={event._id}
      tnew={event.tnewProductionSeason}
    >
      <Box
        pt={[2]}
        sx={{
          width: '100%',
        }}
        aria-live="polite"
      >
        {showImage ? (
          <EventFeatureImage imageAsset={event.eventImage} />
        ) : (
          <Box>
            <MuxVideo
              assetDocument={
                bp > 1 ? event.eventVideo.asset : event.mobileEventVideo.asset
              }
              colorVideo={event?.colorVideo}
            />
          </Box>
        )}
        <Flex
          sx={{
            justifyContent: 'center',
            flexDirection: 'column',
          }}
          pt={[3, 8]}
          pb={[4, 8]}
        >
          <Text
            as="h1"
            variant="largeHeaderReverse"
            sx={{
              textTransform: event?.caseSensitiveTitle ? 'none' : 'uppercase',
            }}
          >
            {event.title && event.title}
          </Text>
          {event.subTitle && (
            <Text
              as="h2"
              variant="copySmallCapsEventListing"
              sx={{
                color: 'red',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
                mt: ['2px', null, '5px', null, null],
              }}
            >
              {event.subTitle}
            </Text>
          )}
        </Flex>
      </Box>
      <Grid
        columns={[1, 2]}
        sx={{
          columnGap: [8, 8],
          rowGap: [2, null, 4],
        }}
      >
        <Box>
          {event.copy && (
            <Box sx={{ display: [null, 'none'] }}>
              <DefaultBlocks blocks={event.copy} reverseColor={true} />
            </Box>
          )}
          {event.eventInformationCopy && (
            <DefaultBlocks
              blocks={event.eventInformationCopy}
              reverseColor={true}
            />
          )}
          {!ticketsOnSale && (event.ticketedEvent || event.ballotedEvent) ? (
            <>
              {ticketsOffSaleCopy ? (
                <Text
                  as="h4"
                  variant="copySmallCapsReverse"
                  sx={{
                    display: 'block',
                    ml: '1.15em',
                    '::selection': { backgroundColor: 'red', color: 'black' },
                  }}
                >
                  {event.ballotedEvent ? 'Ballot opens' : ticketsOffSaleCopy}
                </Text>
              ) : null}
              {ticketsOnSaleDateCopy ? (
                <Box sx={{ mb: '1em' }}>
                  <DefaultBlocks
                    blocks={ticketsOnSaleDateCopy}
                    reverseColor={true}
                  />
                </Box>
              ) : ticketsOnSaleDate ? (
                <Text
                  as="p"
                  variant="copyReverse"
                  sx={{
                    mb: '1em',
                  }}
                >
                  <DefaultBlocks
                    blocks={ticketsOnSaleDateCopy}
                    reverseColor={true}
                  />
                </Text>
              ) : null}
            </>
          ) : null}
        </Box>
        <Box>
          {event.copy && (
            <Box sx={{ display: ['none', 'block'] }}>
              <DefaultBlocks blocks={event.copy} reverseColor={true} />
            </Box>
          )}
          {event.registrationForm && (
            <Box
              sx={{
                mt: [0, 10],
                mb: 5,
              }}
            >
              <Accordion
                i={`register-form-accordion`}
                title={event.registrationFormButtonText || 'Register'}
                reverseColor={true}
                titleVariant="copyReverse"
                hrHeight={['1px', null, null, '2px']}
                mt="0.25em"
              >
                <Form form={event.registrationForm} />
              </Accordion>
              <Line
                color="invertedBlack"
                hrHeight={['1px', null, null, '2px']}
              />
            </Box>
          )}
          {(ticketsOnSale && upcoming) ? (
            <>
              {event.ticketedEvent &&
                (buyTicketsLink || soldOut || event.ticketLinkOverride) && (
                  <Box
                    sx={{
                      mt: [0, 8],
                    }}
                  >
                    <Button
                      as={soldOut ? 'button' : 'a'}
                      href={
                        event.ticketLinkOverride
                          ? event.ticketLinkOverride
                          : buyTicketsLink
                      }
                      target={soldOut ? undefined : '_blank'}
                      variant="primaryReverse"
                      disabled={soldOut}
                      sx={{
                        cursor: soldOut ? 'not-allowed' : 'pointer',
                        margin: 0,
                      }}
                    >
                      {soldOut ? soldOutText : buyTicketsText}
                    </Button>
                  </Box>
                )}
              {((event.ticketedEvent && anySoldOut) || event.ballotedEvent) && !noWaitlist && (
                <Box
                  sx={{
                    mt: 8,
                  }}
                >
                  <Accordion
                    i={`waitlist-form-accordion`}
                    title="Join Waitlist"
                    reverseColor={true}
                    titleVariant="copyReverse"
                    hrHeight={['1px', null, null, '2px']}
                    mt="0.25em"
                  >
                    <Form form={waitlistForm} eventSlug={slug?.current} />
                  </Accordion>
                </Box>
              )}
              {((event.ticketedEvent && anySoldOut) || event.ballotedEvent) && !noResell && (
                <Accordion
                  i={`waitlist-form-accordion`}
                  title="Resell Tickets"
                  reverseColor={true}
                  titleVariant="copyReverse"
                  hrHeight={['1px', null, null, '2px']}
                  mt="0.25em"
                >
                  <Form form={resellForm} eventSlug={slug?.current} />
                </Accordion>
              )}
              {/*event.ballotedEvent && (
                <Box
                  sx={{
                    mt: [0, 8],
                    pb: [5, 5],
                  }}
                >
                  <Accordion
                    i={`waitlist-form-accordion`}
                    title="Enter Ballot"
                    reverseColor={true}
                    titleVariant="copyReverse"
                    hrHeight={['1px', null, null, '2px']}
                    mt="0.25em"
                  >
                    <Form form={ballotForm} eventSlug={slug?.current} />
                  </Accordion>
                  <Line
                    color="invertedBlack"
                    hrHeight={['1px', null, null, '2px']}
                  />
                </Box>
                )*/}
              {event.ticketedEvent && !anySoldOut && !soldOut && (
                <Box sx={{ mb: [5, 5] }} />
              )}
              {((event.ticketedEvent && anySoldOut) || event.ballotedEvent) &&
                (!noResell || !noWaitlist) && (
                  <Line
                    color="invertedBlack"
                    hrHeight={['1px', null, null, '2px']}
                    sx={{ mb: [5, 5] }}
                  />
                )}
            </>
          ) : (upcoming && (event.ticketedEvent || event.ballotedEvent)) ? (
            <Box
              sx={{
                mt: [0, 8],
                pb: [5, 5],
              }}
            >
              <Accordion
                i={`subscribe-form-accordion`}
                title={ticketsOffSaleFormTitle || 'Subscribe'}
                reverseColor={true}
                titleVariant="copyReverse"
                hrHeight={['1px', null, null, '2px']}
                mt="0.25em"
              >
                <Form form={subscribeForm} eventSlug={slug?.current} />
              </Accordion>
              <Line
                color="invertedBlack"
                hrHeight={['1px', null, null, '2px']}
              />
            </Box>
          ) : null}
          <Box
            sx={{
              mt:
                (hasOverrideAccessiblity && hasOverrideAccessiblityCopy) ||
                hasAccessibility
                  ? anySoldOut || !noResell
                    ? 0
                    : 8
                  : 0,
            }}
          >
            {upcoming && event.ballotedEvent && ballotOffSaleNote && (
              <DefaultBlocks blocks={ballotOffSaleNote} reverseColor={true} />
            )}
            {hasOverrideAccessiblity ? (
              hasOverrideAccessiblityCopy ? (
                <DefaultBlocks
                  blocks={overrides.overrideAccessibilityCopy}
                  reverseColor={true}
                />
              ) : null
            ) : (
              event.eventLocations &&
              event.eventLocations.map(({ accessibilityCopy, _id }) => {
                if (accessibilityCopy === undefined) return null
                return (
                  <DefaultBlocks
                    key={_id}
                    blocks={accessibilityCopy}
                    reverseColor={true}
                  />
                )
              })
            )}
            {event.slug.current === 'winter-feast' && (
              <CityOfHobartLogo
                sx={{
                  mt: '1em',
                  ml: '1.15em',
                  maxWidth: ['84px', '100px'],
                }}
              />
            )}
            {event.exclusive && (
              <Flex>
                <Text
                  variant="smallCopyReverse"
                  as="span"
                  sx={{
                    display: 'inline-block',
                    width: '1.15em',
                    mt: '1em',
                    pt: 0,
                    '::selection': { backgroundColor: 'red', color: 'black' },
                  }}
                >
                  <ExclusiveCross
                    fill="red"
                    sx={{
                      width: ['5px', null, '6px', '9px', 'auto'],
                      mb: [0, 0, 0, 0, 0],
                      mr: 1,
                    }}
                  />
                </Text>
                <Text
                  variant="smallCopyReverse"
                  as="span"
                  sx={{
                    display: 'inline-block',
                    mt: '1em',
                    pt: 0,
                    '::selection': { backgroundColor: 'red', color: 'black' },
                  }}
                >
                  Australian exclusive
                </Text>
              </Flex>
            )}
            {event.worldPremiere && (
              <Flex>
                <Text
                  variant="smallCopyReverse"
                  as="span"
                  sx={{
                    display: 'inline-block',
                    width: '1.15em',
                    mt: event.exclusive ? 0 : '1em',
                    '::selection': { backgroundColor: 'red', color: 'black' },
                  }}
                >
                  <WorldCross
                    fill="red"
                    sx={{
                      width: ['5px', null, '6px', '9px', 'auto'],
                      mb: [0, 0, 0, 0, 0],
                      mr: 1,
                    }}
                  />
                </Text>
                <Text
                  variant="smallCopyReverse"
                  as="span"
                  sx={{
                    display: 'inline-block',
                    mt: event.exclusive ? 0 : '1em',
                    '::selection': { backgroundColor: 'red', color: 'black' },
                  }}
                >
                  World premiere
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
      </Grid>
      {!!event.eventMulti && event.eventMulti.showMulti && (
        <Grid
          columns={[1, 2]}
          sx={{
            mt: [8],
            columnGap: [8, 8],
            rowGap: ['1.92em'],
          }}
        >
          {event.eventMulti.artists.map(artist => {
            return !!artist.eventLink ? (
              <Box>
                <Link
                  key={artist._key}
                  href={`/event/${artist.eventLink.slug.current}`}
                  scroll={false}
                  passHref
                >
                  <Box as="a">
                    {!!artist.image.asset && (
                      <EventMultiImage imageAsset={artist.image} />
                    )}
                    <Text
                      variant="copyCapsReverse"
                      sx={{
                        display: 'block',
                        pt: 2,
                      }}
                    >
                      {artist.title}
                    </Text>
                  </Box>
                </Link>
                {artist.copy && (
                  <Box
                    sx={{
                      display: 'block',
                      pt: 2,
                    }}
                  >
                    <DefaultBlocks blocks={artist.copy} reverseColor={true} />
                  </Box>
                )}
                {artist.exclusive && (
                  <Flex>
                    <Text
                      variant="smallCopyReverse"
                      as="span"
                      sx={{
                        display: 'inline-block',
                        width: '1.15em',
                        mt: 0,
                        pt: 0,
                        '::selection': {
                          backgroundColor: 'red',
                          color: 'black',
                        },
                      }}
                    >
                      <ExclusiveCross
                        fill="red"
                        sx={{
                          width: ['5px', null, '6px', '9px', 'auto'],
                          mb: [0, 0, 0, 0, 0],
                          mr: 1,
                        }}
                      />
                    </Text>
                    <Text
                      variant="smallCopyReverse"
                      as="span"
                      sx={{
                        display: 'inline-block',
                        mt: 0,
                        pt: 0,
                        '::selection': {
                          backgroundColor: 'red',
                          color: 'black',
                        },
                      }}
                    >
                      Australian exclusive
                    </Text>
                  </Flex>
                )}
              </Box>
            ) : (
              <Box key={artist._key}>
                {!!artist.image.asset && (
                  <EventMultiImage imageAsset={artist.image} />
                )}
                <Text
                  variant="copyCapsReverse"
                  sx={{
                    display: 'block',
                    pt: 2,
                  }}
                >
                  {artist.title}
                </Text>
                {artist.copy && (
                  <Box
                    sx={{
                      display: 'block',
                      pt: 2,
                    }}
                  >
                    <DefaultBlocks blocks={artist.copy} reverseColor={true} />
                  </Box>
                )}
                {artist.exclusive && (
                  <Flex>
                    <Text
                      variant="smallCopyReverse"
                      as="span"
                      sx={{
                        display: 'inline-block',
                        width: '1.15em',
                        mt: 0,
                        pt: 0,
                        '::selection': {
                          backgroundColor: 'red',
                          color: 'black',
                        },
                      }}
                    >
                      <ExclusiveCross
                        fill="red"
                        sx={{
                          width: ['5px', null, '6px', '9px', 'auto'],
                          mb: [0, 0, 0, 0, 0],
                          mr: 1,
                        }}
                      />
                    </Text>
                    <Text
                      variant="smallCopyReverse"
                      as="span"
                      sx={{
                        display: 'inline-block',
                        mt: 0,
                        pt: 0,
                        '::selection': {
                          backgroundColor: 'red',
                          color: 'black',
                        },
                      }}
                    >
                      Australian exclusive
                    </Text>
                  </Flex>
                )}
              </Box>
            )
          })}
        </Grid>
      )}
      {!!event.winterFeast && <WinterFeast data={event.winterFeast} />}
    </PageLayout>
  )
}

export async function getStaticProps({ params, preview = false }) {
  // child pages will not be used for additional data
  // therefore slug will always be the first element in the array
  const { event } = await getClient(preview).fetch(eventQuery, {
    slug: params.slug[0],
  })

  return {
    props: {
      preview,
      data: {
        event,
      },
    },
    revalidate: 365 * 24 * 60 * 60,
  }
}

export async function getStaticPaths() {
  const { slugs } = await sanityClient.fetch(eventSlugsQuery)
  const { winterfeastSlugs } = await sanityClient.fetch(
    winterfeastEventSlugsQuery,
  )
  return {
    paths: [
      ...slugs.map(slug => ({ params: { slug: [slug] } })),
      ...winterfeastSlugs.map(slug => ({
        params: { slug: ['winter-feast', slug] },
      })),
    ],
    fallback: true, // set to false if next export is required
  }
}
